import React, { useEffect } from 'react';
import {
  Box, Card, CardContent, Grid, Typography, Chip, Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TaskSquare, Warning2, People, Clock } from 'iconsax-react';
import { useTaskStore } from '../../store/task.store';
import { useEmployeeStore } from '../../store/employee.store';
import { isOverdue, getWorkStatus, sortTasks, WORK_STATUS_COLORS, workStatusLabelKey } from '../../utils/task.utils';

interface StatCardProps { title: string; value: string | number; icon: React.ReactNode; color: string; }
function StatCard({ title, value, icon, color }: StatCardProps) {
  return (
    <Card sx={{ height: '100%', cursor: 'default', transition: 'transform 0.15s ease, box-shadow 0.15s ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' } }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: `linear-gradient(135deg, ${color}22 0%, ${color}11 100%)`, border: `1px solid ${color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          {icon}
        </Box>
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>{title}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.5px' }}>{value}</Typography>
      </CardContent>
    </Card>
  );
}

export default function OverviewDashboard() {
  const { t, i18n } = useTranslation();
  const { tasks, fetchTasks }         = useTaskStore();
  const { employees, fetchEmployees } = useEmployeeStore();

  useEffect(() => { 
    fetchTasks(); 
    fetchEmployees(); 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalTasks     = tasks.length;
  const overdueTasks   = tasks.filter(t => isOverdue(t)).length;
  const pendingTasks   = tasks.filter(t => t.status === 'needsAction' && !isOverdue(t)).length;
  const employeesWithAccess = employees.filter(e => e.google_access_status === 'approved').length;

  const recentTasks = sortTasks(tasks, 'updated', 'desc').slice(0, 5);
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.3px' }}>
          {t('dashboard.welcome')} 👋
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', mt: 0.5 }}>
          {t('dashboard.todaySubtitle')}
        </Typography>
      </Box>

      {/* Stat Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title={t('dashboard.totalTasks')} value={totalTasks} icon={<TaskSquare size={22} variant="Bold" color="#3B82F6" />} color="#3B82F6" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title={t('dashboard.pendingTasks')} value={pendingTasks} icon={<Clock size={22} variant="Bold" color="#F59E0B" />} color="#F59E0B" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title={t('dashboard.teamMembers')} value={employeesWithAccess} icon={<People size={22} variant="Bold" color="#10B981" />} color="#10B981" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard title={t('dashboard.overdueTasks')} value={overdueTasks} icon={<Warning2 size={22} variant="Bold" color="#EF4444" />} color="#EF4444" />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        {/* Recent Tasks */}
        <Grid item xs={12}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <Clock size={18} color="#6B7280" />
                <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>{t('dashboard.recentTasks')}</Typography>
              </Box>
              {recentTasks.length === 0 ? (
                <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', textAlign: 'center', py: 3 }}>
                  {t('tasks.noTasks')}
                </Typography>
              ) : (
                <Stack spacing={0.5}>
                  {recentTasks.map(task => {
                    const workStatus = getWorkStatus(task);
                    const cfg = WORK_STATUS_COLORS[workStatus];
                    return (
                      <Box key={`${task.employee_id}:${task.task_list_id}:${task.google_task_id}`} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1.5, py: 1.25, borderRadius: '10px', '&:hover': { bgcolor: '#F4F6FA' }, cursor: 'pointer', transition: 'background 0.12s ease' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' }}>{task.title}</Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {task.employee_name}
                            {task.due && ` · ${t('common.due')} ${new Date(task.due).toLocaleDateString(locale, { month: 'short', day: 'numeric' })}`}
                          </Typography>
                        </Box>
                        <Chip label={t(workStatusLabelKey(workStatus))} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 500, fontSize: '0.75rem', border: 'none' }} />
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
